// UserContext.tsx
import { createSignal, createMemo, createContext, createResource, useContext, JSX, createEffect } from "solid-js";
import { createAsync, revalidate, type AccessorWithLatest } from "@solidjs/router";
import { isServer } from "solid-js/web";

import { type Subscription, type LogAction, type RIASRecordType, isProd } from "~/lib";
import { getUserInfoQuery } from "~/lib/queries";
import { ADMIN } from "~/_server/constants";

import { logAction as serverLogAction } from "~/_server/dynamoDB";

type User = {
	sub: string;
	email: string;
	"cognito:groups"?: string[];
	impersonating?: boolean;
	profile?: string;
};

type UserContextValue = {
	user: AccessorWithLatest<any>;
	logAction: ( group: RIASRecordType, action: LogAction, metadata?: Record<string, any> ) => Promise<void>;
	// Not provided by UserProvider and not read anywhere, hence optional.
	subscriptions?: () => Subscription[];
	inActiveSubscriptions?: () => Subscription[];
	sessionId: () => number;
	impersonationId: () => number;
	setImpersonation: ( n: number ) => void;
	impersonationName?: () => string | null;
	isLoggedIn: () => boolean;
	isImpersonating: () => boolean;
	userIs: ( group: string ) => boolean;
	refetchUser: () => void;
	sub: () => string;
};

export const UserContext = createContext<UserContextValue>();

export const useRIASApp = (): UserContextValue => useContext( UserContext ) as UserContextValue;

export const UserProvider = ( props: { children: JSX.Element } ) => {
	const [impersonationId, setImpersonationId] = createSignal<number>( 0 );
	const [sessionId, setSessionId] = createSignal<number>( 0 );

	const user = createAsync( () => getUserInfoQuery() );

	createEffect( () => {
		const imp = impersonationId();
		const u = user();
		const id = ( imp ?? 0 ) !== 0 ? imp : parseInt( u?.profile ?? 0 );

		// Diagnostic: the session id should never settle on 0 once the Cognito
		// user has resolved. If it does, the profile claim is missing and the
		// dashboard will wrongly treat the user as a non-certifier. Transient
		// zeros (user not resolved yet) are expected and not logged.
		if ( isProd && u && id === 0 ) {
			console.log( "[sessionId=0] user resolved without a usable profile", { profile: u?.profile, sub: u?.sub, groups: u?.groups } );
		}

		setSessionId( id );
	} );

	const userIs = ( group: string ): boolean =>
		( Array.isArray( user()?.groups ) && ( user()?.groups ).includes( group ) ) ?? false;

	const isLoggedIn = createMemo( () => Boolean( user()?.sub ) );

	const isImpersonating = createMemo( () => Boolean( impersonationId() > 0 ) );

	const logAction = async (
		group: RIASRecordType,
		action: LogAction,
		metadata: Record<string, any> = {},
	): Promise<void> => {
		/*const locationMetadata = {
		...metadata,
		pathname: location.pathname,
		search: location.search,
		state: location.state
		};*/

		serverLogAction( sessionId(), group, action, metadata, `USER#${parseInt( user()?.profile ?? 0 )}` );
	}

	createEffect( async () => {
		if ( !isServer ) {
			if ( user()?.groups.includes( ADMIN ) ) {
				if ( impersonationId() && impersonationId() > -1 ) {
					await fetch( '/api/impersonate', {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify( { impersonateId: impersonationId() > 0 ? impersonationId() : null } )
					} );
				}
			}
		}
	} );

	return (
		<UserContext.Provider
			value={{
				user,
				// Accessor, not a snapshot: read once at provider render, user()
				// is usually still pending, so a static string would stay "" and
				// the logbook would never see the Cognito sub it keys on.
				sub: () => user()?.sub ?? "",
				logAction,
				sessionId,
				impersonationId,
				setImpersonation: ( n: number ) => setImpersonationId( n ),
				isLoggedIn,
				isImpersonating,
				userIs,
				refetchUser: () => revalidate( getUserInfoQuery.keyFor() ),
			}}
		>
			{props.children}
		</UserContext.Provider>
	);
};

